import { useRef } from 'react';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Badge from '@cloudscape-design/components/badge';
import Header from '@cloudscape-design/components/header';
import type {
  Asset,
  AssetCategory,
  AssetStatus,
  ServerAsset,
  ElementalAsset,
  NetworkDeviceAsset,
} from '@/types/storageInventory';
import { displayOptionalField, formatDate } from '@/utils/assetUtils';
import { getWarrantyStatus } from '@/utils/warrantyUtils';
import { uploadPhotoFiles } from '@/lib/photoUpload';
import { PhotoImage, resolvePhotoSrc } from './PhotoImage';

interface AssetDetailPanelProps {
  visible: boolean;
  asset: Asset | undefined;
  category: AssetCategory;
  existingSites: string[];
  existingMakes: string[];
  onEdit: (asset: Asset) => void;
  onAddPhoto: (asset: Asset, images: string[]) => void;
  onDelete: (asset: Asset) => void;
  onDismiss: () => void;
}

function renderStatusBadge(status: AssetStatus) {
  switch (status) {
    case 'in storage':
      return <StatusIndicator type="info">{status}</StatusIndicator>;
    case 'in production':
      return <StatusIndicator type="success">{status}</StatusIndicator>;
    case 'has issues':
      return <StatusIndicator type="error">{status}</StatusIndicator>;
    case 'decommissioned':
      return <StatusIndicator type="stopped">{status}</StatusIndicator>;
    case 'Missing/Unknown Can Not Find During Audit (Infradev-1803)':
      return <StatusIndicator type="warning">{status}</StatusIndicator>;
    case 'unknown':
    default:
      return <Badge color="grey">{status}</Badge>;
  }
}

function renderWarrantyBadge(supportEndDate: string | undefined) {
  if (!supportEndDate) return <span>-</span>;
  const status = getWarrantyStatus(supportEndDate);
  switch (status) {
    case 'expired':
      return (
        <SpaceBetween direction="horizontal" size="xs">
          <span>{formatDate(supportEndDate)}</span>
          <StatusIndicator type="error">Expired</StatusIndicator>
        </SpaceBetween>
      );
    case 'expiring-soon':
      return (
        <SpaceBetween direction="horizontal" size="xs">
          <span>{formatDate(supportEndDate)}</span>
          <StatusIndicator type="warning">Expiring soon</StatusIndicator>
        </SpaceBetween>
      );
    default:
      return (
        <SpaceBetween direction="horizontal" size="xs">
          <span>{formatDate(supportEndDate)}</span>
          <StatusIndicator type="success">Valid</StatusIndicator>
        </SpaceBetween>
      );
  }
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <div>{children}</div>
    </div>
  );
}

function ServerDetails({ asset }: { asset: ServerAsset }) {
  return (
    <SpaceBetween size="l">
      <ColumnLayout columns={2} variant="text-grid">
        <DetailRow label="Site">{asset.site}</DetailRow>
        <DetailRow label="Hostname">{asset.hostname}</DetailRow>
        <DetailRow label="Make">{asset.make}</DetailRow>
        <DetailRow label="Model">{asset.model}</DetailRow>
        <DetailRow label="Service Tag">{asset.make?.toLowerCase() === 'dell' ? <a href={`https://www.dell.com/support/home/en-us/product-support/servicetag/${asset.service_tag}/overview`} target="_blank" rel="noopener noreferrer" style={{ color: '#0073bb' }}>{asset.service_tag}</a> : asset.service_tag}</DetailRow>
        <DetailRow label="Express Service Code">{asset.express_service_code || '-'}</DetailRow>
        <DetailRow label="Status">{renderStatusBadge(asset.status)}</DetailRow>
        <DetailRow label="Ship Date">{formatDate(asset.ship_date)}</DetailRow>
        <DetailRow label="Support End Date">{renderWarrantyBadge(asset.support_end_date)}</DetailRow>
        <DetailRow label="Last Known Contact">{displayOptionalField(asset.last_known_contact)}</DetailRow>
      </ColumnLayout>
      <ColumnLayout columns={2} variant="text-grid">
        <DetailRow label="CPU Model">{displayOptionalField(asset.cpu_model)}</DetailRow>
        <DetailRow label="CPU Quantity">{displayOptionalField(asset.cpu_quantity)}</DetailRow>
        <DetailRow label="RAM Model">{displayOptionalField(asset.ram_model)}</DetailRow>
        <DetailRow label="RAM Quantity">{displayOptionalField(asset.ram_quantity)}</DetailRow>
        <DetailRow label="Disk Model">{displayOptionalField(asset.disk_model)}</DetailRow>
        <DetailRow label="Disk Quantity">{displayOptionalField(asset.disk_quantity)}</DetailRow>
        <DetailRow label="Embedded NICs">{displayOptionalField(asset.embedded_nics)}</DetailRow>
        <DetailRow label="Integrated NICs">{displayOptionalField(asset.integrated_nics)}</DetailRow>
        <DetailRow label="Add-on Cards">{displayOptionalField(asset.addon_cards)}</DetailRow>
      </ColumnLayout>
      <ColumnLayout columns={2} variant="text-grid">
        <DetailRow label="Purchase Order">{displayOptionalField(asset.purchase_order)}</DetailRow>
        <DetailRow label="Cost">{asset.cost ? `$${asset.cost.toLocaleString()}` : '-'}</DetailRow>
        <DetailRow label="Location Detail">{displayOptionalField(asset.location_detail)}</DetailRow>
        <DetailRow label="Notes">{displayOptionalField(asset.notes)}</DetailRow>
      </ColumnLayout>
    </SpaceBetween>
  );
}

function ElementalDetails({ asset }: { asset: ElementalAsset }) {
  return (
    <SpaceBetween size="l">
      <ColumnLayout columns={2} variant="text-grid">
        <DetailRow label="Site">{asset.site}</DetailRow>
        <DetailRow label="Series">{asset.series}</DetailRow>
        <DetailRow label="Manufacturer">{asset.manufacturer}</DetailRow>
        <DetailRow label="Model">{asset.model}</DetailRow>
        <DetailRow label="Service Tag">{asset.manufacturer?.toLowerCase() === 'dell' || asset.manufacturer?.toLowerCase() === 'aws' ? <a href={`https://www.dell.com/support/home/en-us/product-support/servicetag/${asset.service_tag}/overview`} target="_blank" rel="noopener noreferrer" style={{ color: '#0073bb' }}>{asset.service_tag}</a> : asset.service_tag}</DetailRow>
        <DetailRow label="Express Service Code">{asset.express_service_code || '-'}</DetailRow>
        <DetailRow label="Boxed/Unboxed">{asset.boxed_status}</DetailRow>
        <DetailRow label="Condition">{asset.condition}</DetailRow>
        <DetailRow label="Location">{displayOptionalField(asset.location)}</DetailRow>
        <DetailRow label="To Be Decommissioned">
          {asset.to_be_decommissioned ? (
            <StatusIndicator type="warning">Yes</StatusIndicator>
          ) : (
            <span>No</span>
          )}
        </DetailRow>
        <DetailRow label="Last Known Contact">{displayOptionalField(asset.last_known_contact)}</DetailRow>
      </ColumnLayout>
      <ColumnLayout columns={2} variant="text-grid">
        <DetailRow label="Purchase Order">{displayOptionalField(asset.purchase_order)}</DetailRow>
        <DetailRow label="Cost">{asset.cost ? `$${asset.cost.toLocaleString()}` : '-'}</DetailRow>
        <DetailRow label="Location Detail">{displayOptionalField(asset.location_detail)}</DetailRow>
        <DetailRow label="Notes">{displayOptionalField(asset.notes)}</DetailRow>
      </ColumnLayout>
    </SpaceBetween>
  );
}

function NetworkDeviceDetails({ asset }: { asset: NetworkDeviceAsset }) {
  return (
    <SpaceBetween size="l">
      <ColumnLayout columns={2} variant="text-grid">
        <DetailRow label="Site">{asset.site}</DetailRow>
        <DetailRow label="Hostname">{displayOptionalField(asset.hostname)}</DetailRow>
        <DetailRow label="Make">{asset.make}</DetailRow>
        <DetailRow label="Model">{asset.model}</DetailRow>
        <DetailRow label="Serial Number">{asset.serial_num}</DetailRow>
        <DetailRow label="Status">{renderStatusBadge(asset.status)}</DetailRow>
        <DetailRow label="Support End Date">{renderWarrantyBadge(asset.support_end_date)}</DetailRow>
        <DetailRow label="Quantity">{asset.quantity}</DetailRow>
        <DetailRow label="Last Known Contact">{displayOptionalField(asset.last_known_contact)}</DetailRow>
      </ColumnLayout>
      <ColumnLayout columns={2} variant="text-grid">
        <DetailRow label="Customer Order">{displayOptionalField(asset.customer_order)}</DetailRow>
        <DetailRow label="Shipping Box">{displayOptionalField(asset.shipping_box)}</DetailRow>
        <DetailRow label="Tracking Number">{displayOptionalField(asset.tracking_num)}</DetailRow>
        <DetailRow label="Purchase Order">{displayOptionalField(asset.purchase_order)}</DetailRow>
        <DetailRow label="Cost">{asset.cost ? `$${asset.cost.toLocaleString()}` : '-'}</DetailRow>
        <DetailRow label="Location Detail">{displayOptionalField(asset.location_detail)}</DetailRow>
        <DetailRow label="Notes">{displayOptionalField(asset.notes)}</DetailRow>
      </ColumnLayout>
    </SpaceBetween>
  );
}

function getAssetTitle(asset: Asset, category: AssetCategory): string {
  if (category === 'servers') {
    const s = asset as ServerAsset;
    return s.hostname || s.service_tag;
  }
  if (category === 'elementals') {
    const e = asset as ElementalAsset;
    return `${e.manufacturer} ${e.model} (${e.service_tag})`;
  }
  const n = asset as NetworkDeviceAsset;
  return n.hostname || `${n.make} ${n.model} (${n.serial_num})`;
}

export function AssetDetailPanel({
  visible,
  asset,
  category,
  onEdit,
  onAddPhoto,
  onDelete,
  onDismiss,
}: AssetDetailPanelProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!asset) return null;

  const title = getAssetTitle(asset, category);

  const handleCopyLink = () => {
    const url = `${window.location.origin}/storage-inventory?tab=${category}&asset=${asset.id}`;
    navigator.clipboard.writeText(url);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !asset) return;

    try {
      // Upload each photo and append the returned keys to the asset's images.
      const keys = await uploadPhotoFiles(category, files);
      if (keys.length > 0) {
        onAddPhoto(asset, [...(asset.images || []), ...keys]);
      }
    } catch (err) {
      console.error('Photo upload failed', err);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={
        <Header
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                iconName="copy"
                variant="icon"
                onClick={handleCopyLink}
                ariaLabel="Copy link to asset"
              />
              <Button
                iconName="edit"
                variant="icon"
                onClick={() => onEdit(asset)}
                ariaLabel="Edit asset"
              />
            </SpaceBetween>
          }
        >
          {title}
        </Header>
      }
      size="large"
      footer={
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => { onDelete(asset); onDismiss(); }}>
            <span style={{ color: '#d13212' }}>Delete asset</span>
          </Button>
          <Box float="right">
            <Button onClick={onDismiss}>Close</Button>
          </Box>
        </SpaceBetween>
      }
    >
      <SpaceBetween size="l">
        {/* Photos section — at the top for quick access */}
        <SpaceBetween size="s">
          <Box variant="h3">Photos</Box>
          {asset.images && asset.images.length > 0 ? (
            <SpaceBetween direction="horizontal" size="s">
              {asset.images.map((img, idx) => (
                <PhotoImage
                  key={idx}
                  photo={img}
                  alt={`Asset photo ${idx + 1}`}
                  style={{ width: 200, height: 150, objectFit: 'cover', borderRadius: 4, border: '1px solid #d1d5db', cursor: 'pointer' }}
                  onClick={() => { resolvePhotoSrc(img).then((url) => { if (url) window.open(url, '_blank'); }); }}
                />
              ))}
            </SpaceBetween>
          ) : (
            <Box variant="p" color="text-body-secondary">No photos attached</Box>
          )}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              multiple
              onChange={handlePhotoUpload}
              style={{ display: 'none' }}
            />
            <Button iconName="upload" onClick={() => fileInputRef.current?.click()}>
              Add photo
            </Button>
          </div>
        </SpaceBetween>

        {category === 'servers' && <ServerDetails asset={asset as ServerAsset} />}
        {category === 'elementals' && <ElementalDetails asset={asset as ElementalAsset} />}
        {category === 'network_devices' && <NetworkDeviceDetails asset={asset as NetworkDeviceAsset} />}
      </SpaceBetween>
    </Modal>
  );
}
